import type { LineItemFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { defineMessages, useIntl } from 'react-intl';
import { getBridge } from '@packages/shared/src/utils/getBridge';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import type { GlycerinCartProduct } from '@packages/tracking/src/types/types';
import type {
  GTMEventGlycerinRemoveFromCart,
  GTMEventGlycerinAddToWishList,
} from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { dispatchButtonTracking } from '@packages/shared/src/ui/Button/dispatchButtonTracking';
import { RemoveFromBasketDocument } from '@packages/gql/generated/shopping/RemoveFromBasketDocument';
import { AddToWishlistDocument } from '@packages/gql/generated/shopping/AddToWishlistDocument';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import type { ButtonHTMLAttributes } from 'react';
import { buttonStylingTailwind, iconStylingTailwind } from './Actions.styles';
import { useDeleteFromOrderableBasket } from '../../hooks/useOrderableBasketActions';

type BasketAddToWishlistActionProps = {
  mode?: 'basket';
  lineItemId: LineItemFragmentFragment['id'];
  sku: LineItemFragmentFragment['product']['sku'];
  glycerinProduct: GlycerinCartProduct;
  onSuccess?: never;
};

type CheckoutAddToWishlistActionProps = {
  mode: 'checkout';
  lineItemId: string;
  sku: string;
  glycerinProduct: GlycerinCartProduct;
  onSuccess?: never;
};

export type AddToWishlistActionProps =
  | BasketAddToWishlistActionProps
  | CheckoutAddToWishlistActionProps;
const messages = defineMessages({
  buttonText: {
    id: 'AddToWishlistAction.ButtonText',
    defaultMessage: 'Merken',
    description: 'this will be displayed on the button',
  },
  successMessage: {
    id: 'AddToWishlistAction.SuccessMessage',
    defaultMessage: 'Dein Artikel wurde erfolgreich auf den Merkzettel verschoben.',
    description: 'this will be displayed on notification provider',
  },
});

export const AddToWishlistAction: React.FC<
  AddToWishlistActionProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
> = ({ mode, lineItemId, sku, glycerinProduct, onClick, onSuccess, ...props }) => {
  const { formatMessage, locale } = useIntl();
  const dispatchGlycerin = useTracking();
  const [basketState, executeBasketMutation] = useAuthMutation(RemoveFromBasketDocument);
  const [wishlistState, executeWishlistMutation] = useAuthMutation(AddToWishlistDocument);
  const [checkoutBasketState, executeCheckoutBasketMutation] = useDeleteFromOrderableBasket();
  const { addNotification } = useNotification();

  const isFetching = basketState.fetching || wishlistState.fetching || checkoutBasketState.fetching;

  const onAddWishlistHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    dispatchButtonTracking({
      eventDispatcher: dispatchGlycerin,
      type: 'button',
      clickTrackingProps: {
        category: 'Artikel Merken',
        label: formatMessage(messages.buttonText),
      },
    });

    const canExecuteCheckout =
      mode === 'checkout' &&
      !checkoutBasketState.fetching &&
      !!lineItemId &&
      !wishlistState.fetching &&
      !!sku;

    if (canExecuteCheckout) {
      onClick?.(event);
      // Remove from orderable basket and add to wishlist in parallel
      Promise.allSettled([
        executeWishlistMutation({ locale, sku, unspecified: false }),
        executeCheckoutBasketMutation({ locale, pli: lineItemId }),
      ]).then(([wishlistResult, basketResult]) => {
        if (wishlistResult.status === 'fulfilled' && basketResult.status === 'fulfilled') {
          const resultData = basketResult.value.data?.deleteProductFromOrderableBasket;
          if (resultData?.__typename === 'CheckoutChangedOrderableBasket' && resultData.success) {
            getBridge().updateWishlist();
            addNotification({
              severity: 'success',
              message: formatMessage(messages.successMessage),
              duration: 5000,
              onClose: () => {},
            });
            dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
              event: 'RemoveFromCart',
              RemoveFromCartData: { removedProducts: [glycerinProduct] },
            });
            dispatchGlycerin<GTMEventGlycerinAddToWishList>({
              event: 'AddToWishList',
              AddToWishListData: { addedProducts: [glycerinProduct] },
            });
          }
        }
      });
      return;
    }

    if (!basketState.fetching && lineItemId && !wishlistState.fetching && sku) {
      onClick?.(event);
      // We need to wait for both mutations to finish before we can decide if it was successful or not
      Promise.allSettled([
        executeWishlistMutation({ locale, sku, unspecified: false }),
        executeBasketMutation({ lineItemId, locale }),
      ]).then(([wishlistResult, basketResult]) => {
        if (wishlistResult.status === 'fulfilled' && basketResult.status === 'fulfilled') {
          const basket = getFragmentData(
            BasketFragmentFragmentDoc,
            basketResult.value.data?.removeFromBasket,
          );

          if (basket && basket.success) {
            getBridge().updateWishlist();
            getBridge().updateBasket();
            addNotification({
              severity: 'success',
              message: formatMessage(messages.successMessage),
              duration: 5000,
              onClose: () => {},
            });
            dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
              event: 'RemoveFromCart',
              RemoveFromCartData: {
                removedProducts: [glycerinProduct],
              },
            });
            dispatchGlycerin<GTMEventGlycerinAddToWishList>({
              event: 'AddToWishList',
              AddToWishListData: {
                addedProducts: [glycerinProduct],
              },
            });
          }
        }
      });
    }
  };
  return (
    <Button
      className={buttonStylingTailwind}
      onClick={onAddWishlistHandler}
      data-testid="wishlist-action"
      variant="icon-flat"
      size="base"
      disabled={isFetching}
      {...props}
    >
      {isFetching ? (
        <CircularProgress className={iconStylingTailwind} />
      ) : (
        <div className="icon-emp-wishlist size-4 md:size-3" />
      )}
      {formatMessage(messages.buttonText)}
    </Button>
  );
};
